import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { SEO } from '../../components/SEO';
import {
  Rocket, Code, FlaskConical, Cog, Calculator, Bot, Star,
  Loader2, Filter, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  code: { icon: Code, color: 'bg-blue-100 text-blue-700', label: 'Coding' },
  robot: { icon: Bot, color: 'bg-orange-100 text-orange-700', label: 'Robotics' },
  science: { icon: FlaskConical, color: 'bg-rose-100 text-rose-700', label: 'Science' },
  engineering: { icon: Cog, color: 'bg-emerald-100 text-emerald-700', label: 'Engineering' },
  math: { icon: Calculator, color: 'bg-purple-100 text-purple-700', label: 'Math' },
};

export default function StudentShowcasePage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetch(`${API_URL}/api/projects/gallery/`)
      .then(r => r.json())
      .then(d => setProjects(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'all' ? projects : projects.filter(p => p.project_type === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-rsc-navy" />
      </div>
    );
  }

  return (
    <div data-testid="student-showcase-page">
      <SEO title="Student Project Showcase" description="See what Ready Set STEM students are building! Browse coding projects, robots, science experiments, and engineering designs from K-8 learners." />

      {/* Hero */}
      <section className="relative py-16 bg-gradient-to-br from-rsc-navy via-[#1a3a6d] to-[#0f2847] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(251,191,36,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(251,191,36,0.2) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
        <div className="container-app relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-rsc-yellow/30 bg-rsc-yellow/10 text-rsc-yellow text-sm font-medium mb-4">
            <Star className="w-4 h-4" /> STUDENT PROJECTS
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-3">
            What Our Students Are <span className="text-rsc-yellow">Building</span>
          </h1>
          <p className="text-white/60 max-w-lg mx-auto text-base">
            Games, robots, experiments, websites, and more — built by K-8 learners in our STEM programs.
          </p>
        </div>
      </section>

      {/* Filter + Grid */}
      <section className="py-10 bg-gray-50 min-h-[40vh]">
        <div className="container-app">
          {/* Filter Pills */}
          <div className="flex flex-wrap gap-2 mb-8 justify-center" data-testid="project-filters">
            <Button variant={filter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('all')}
              className={filter === 'all' ? 'bg-rsc-navy' : ''}>
              All Projects ({projects.length})
            </Button>
            {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
              const count = projects.filter(p => p.project_type === key).length;
              if (count === 0) return null;
              const Icon = cfg.icon;
              return (
                <Button key={key} variant={filter === key ? 'default' : 'outline'} size="sm"
                  onClick={() => setFilter(key)} className={filter === key ? 'bg-rsc-navy' : ''}>
                  <Icon className="h-3.5 w-3.5 mr-1.5" /> {cfg.label} ({count})
                </Button>
              );
            })}
          </div>

          {/* Projects Grid */}
          {filtered.length === 0 ? (
            <div className="text-center py-12">
              <Rocket className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No projects in this category yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
              {filtered.map((project) => {
                const cfg = TYPE_CONFIG[project.project_type] || TYPE_CONFIG.code;
                const Icon = cfg.icon;
                return (
                  <Card key={project.id} className={`overflow-hidden hover:shadow-lg transition-shadow ${project.is_featured ? 'ring-2 ring-rsc-yellow/30' : ''}`}
                    data-testid={`project-${project.id}`}>
                    {project.image_urls?.[0] && (
                      <div className="h-48 overflow-hidden">
                        <img src={project.image_urls[0]} alt={project.title}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                      </div>
                    )}
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-2">
                        <Badge className={`${cfg.color} text-xs`}>
                          <Icon className="h-3 w-3 mr-1" /> {cfg.label}
                        </Badge>
                        {project.is_featured && (
                          <Badge className="bg-rsc-yellow/20 text-rsc-yellow border-rsc-yellow/30 text-[10px]">
                            <Star className="h-3 w-3 mr-0.5 fill-current" /> Featured
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-bold text-gray-900 mb-1">{project.title}</h3>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-3">{project.description}</p>
                      <div className="flex flex-wrap gap-1 mb-3">
                        {project.technologies?.map(t => (
                          <span key={t} className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{t}</span>
                        ))}
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span>{project.student_name} {project.student_grade && `· Grade ${project.student_grade}`}</span>
                        {project.course_title && <span className="truncate max-w-[120px]">{project.course_title}</span>}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
